import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { ProfessorStudentGroupService } from '../../../../core/services/groups/professor-student-group.service';
import {
  StudentGroup,
  StudentGroupMember,
} from '../../../../core/interfaces/student-group';
import { ErrorStateComponent } from '../../../ui/error-state/error-state.component';

@Component({
  selector: 'app-teacher-groups',
  standalone: true,
  imports: [CommonModule, TranslateModule, ErrorStateComponent],
  templateUrl: './groups.component.html',
  styleUrl: './groups.component.css',
})
export class TeacherGroupsComponent implements OnInit {
  private readonly service = inject(ProfessorStudentGroupService);

  readonly groups = signal<StudentGroup[]>([]);
  readonly loading = signal<boolean>(false);
  readonly errored = signal<boolean>(false);

  readonly selectedGroup = signal<StudentGroup | null>(null);
  readonly members = signal<StudentGroupMember[]>([]);
  readonly loadingMembers = signal<boolean>(false);
  readonly membersErrored = signal<boolean>(false);

  readonly query = signal<string>('');

  readonly filteredGroups = computed(() => {
    const q = this.query().trim().toLowerCase();
    if (!q) return this.groups();
    return this.groups().filter((g) =>
      `${g.name} ${g.description ?? ''} ${g.learningPathTitle ?? ''}`
        .toLowerCase()
        .includes(q),
    );
  });

  ngOnInit(): void {
    this.loadGroups();
  }

  onSearch(value: string): void {
    this.query.set(value);
  }

  selectGroup(group: StudentGroup): void {
    this.selectedGroup.set(group);
    // If members are already in the group payload, use them; else fetch
    if (group.members && group.members.length > 0) {
      this.membersErrored.set(false);
      this.members.set(group.members);
    } else {
      this.fetchMembers(group.id);
    }
  }

  closeDetails(): void {
    this.selectedGroup.set(null);
    this.members.set([]);
    this.membersErrored.set(false);
  }

  /** Rechargement manuel de la liste des groupes, déclenché par <app-error-state>. */
  reload(): void {
    this.loadGroups();
  }

  /** Rechargement manuel des membres du groupe sélectionné. */
  reloadMembers(): void {
    const group = this.selectedGroup();
    if (group) this.fetchMembers(group.id);
  }

  // ─────────────────────────────────────────────────────────
  private loadGroups(): void {
    this.loading.set(true);
    this.errored.set(false);
    this.service.myGroups().subscribe({
      next: (res) => {
        this.groups.set(res?.data ?? []);
        this.loading.set(false);
      },
      error: () => {
        this.groups.set([]);
        this.errored.set(true);
        this.loading.set(false);
      },
    });
  }

  private fetchMembers(groupId: number): void {
    this.loadingMembers.set(true);
    this.membersErrored.set(false);
    this.service.members(groupId).subscribe({
      next: (res) => {
        this.members.set(res?.data ?? []);
        this.loadingMembers.set(false);
      },
      error: () => {
        this.members.set([]);
        this.membersErrored.set(true);
        this.loadingMembers.set(false);
      },
    });
  }
}
